/**
 * A service's configuration (variables, secrets, scheduled jobs) and the project's mail (vfs-service src/appconfig,
 * in the person's sandbox pod). Same base URL, authorized fetch and ACL headers as the Quality and Data tabs, and their
 * errors (DataError; a 404 without a JSON body: the sandbox runs an older vfs-service, `code: 'sandbox_outdated'`).
 * Reads need a viewer, writes an editor; a viewer sees the names of the variables and secrets, never their values, and
 * nobody ever gets a secret's value back (the API has no way to).
 */
import config from '../config';
import { getAuthorizedFetch } from '../utils/fetchRegistry';
import { buildAclContextHeaders } from '../utils/aclContext';
import { DataError, fail } from './DataService';

const base = () => (config.vfsServiceUrl || '').replace(/\/$/, '');
const projectUrl = (projectId, path) => `${base()}/api/v1/projects/${encodeURIComponent(projectId)}${path}`;
const seg = (value) => encodeURIComponent(String(value));
const svcPath = (service) => `/app-config/services/${seg(service)}`;

async function call(url, { method = 'GET', body } = {}) {
  if (!base()) { throw new DataError('vfs-service is not configured', 0, null); }
  const json = body !== undefined;
  const response = await getAuthorizedFetch()(url, {
    method,
    headers: { Accept: 'application/json', ...buildAclContextHeaders(), ...(json ? { 'Content-Type': 'application/json' } : {}) },
    ...(json ? { body: JSON.stringify(body) } : {}),
  });
  if (!response.ok) { throw await fail(response); }
  if (response.status === 204) { return {}; }
  return (await response.json().catch(() => null)) || {};
}

/** The project's services with what each has configured, the mail relay and the note about a sleeping sandbox. */
export const getAppConfig = (projectId) => call(projectUrl(projectId, '/app-config'));

/** One service: `{ vars, secrets, jobs, needsRestart, canEdit, errors }`. */
export const getServiceConfig = (projectId, service) => call(projectUrl(projectId, svcPath(service)));

export const setVariable = (projectId, service, name, value) => call(projectUrl(projectId, `${svcPath(service)}/vars/${seg(name)}`), { method: 'PUT', body: { value } });
export const deleteVariable = (projectId, service, name) => call(projectUrl(projectId, `${svcPath(service)}/vars/${seg(name)}`), { method: 'DELETE' });

/** Saves a secret (write-only): the answer has its name and when, never the value. */
export const setSecret = (projectId, service, name, value) => call(projectUrl(projectId, `${svcPath(service)}/secrets/${seg(name)}`), { method: 'PUT', body: { value } });
/**
 * Saves a file secret (write-only), e.g. a service account's JSON key: `file` is `{ content, encoding: 'base64' | 'utf8',
 * fileName, envName?, mountPath? }` (without `content`, only the names and path change). The answer has its name, file
 * name, size and the path the service reads it at, never the content.
 */
export const setFileSecret = (projectId, service, name, file) => call(projectUrl(projectId, `${svcPath(service)}/secrets/${seg(name)}`), { method: 'PUT', body: { file } });
export const deleteSecret = (projectId, service, name) => call(projectUrl(projectId, `${svcPath(service)}/secrets/${seg(name)}`), { method: 'DELETE' });

/** Adds or replaces a job by name. */
export const saveJob = (projectId, service, job) => call(projectUrl(projectId, `${svcPath(service)}/jobs/${seg(job.name)}`), { method: 'PUT', body: { job } });
export const deleteJob = (projectId, service, name) => call(projectUrl(projectId, `${svcPath(service)}/jobs/${seg(name)}`), { method: 'DELETE' });
export const runJob = (projectId, service, name) => call(projectUrl(projectId, `${svcPath(service)}/jobs/${seg(name)}/run`), { method: 'POST', body: {} });
export const listRuns = (projectId, service, name) => call(projectUrl(projectId, `${svcPath(service)}/jobs/${seg(name)}/runs`));
/** One run with its log (editors). */
export const getRun = (projectId, service, name, runId) => call(projectUrl(projectId, `${svcPath(service)}/jobs/${seg(name)}/runs/${seg(runId)}`));

/** Quality › Secrets › Move to secrets: `{ name, service, patched, prompt?, rotate }`. */
export const moveFindingToSecret = (projectId, { findingId, name, mode }) => call(projectUrl(projectId, '/app-config/move-secret'), { method: 'POST', body: { findingId, ...(name ? { name } : {}), mode } });

/** What a delivery carries (no secret values): `{ version, services: { svc: { env, secrets, jobs } }, notes }`. */
export const getHandoff = (projectId) => call(projectUrl(projectId, '/app-config/handoff'));

/** The relay's state and the messages: `{ relay, messages, canRead }`. */
export const getMail = (projectId, { status } = {}) => call(projectUrl(projectId, `/mail${status ? `?status=${seg(status)}` : ''}`));
export const getMailMessage = (projectId, id) => call(projectUrl(projectId, `/mail/${seg(id)}`));
export const clearMail = (projectId) => call(projectUrl(projectId, '/mail'), { method: 'DELETE' });
export const mailRawUrl = (projectId, id) => projectUrl(projectId, `/mail/${seg(id)}/raw`);

/** Downloads a message as .eml (with the person's sign-in). */
export async function downloadMail(projectId, id) {
  const response = await getAuthorizedFetch()(mailRawUrl(projectId, id), { headers: buildAclContextHeaders() });
  if (!response.ok) { throw await fail(response); }
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${id}.eml`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
